import type { ChangeEvent, SyntheticEvent } from 'react'
import { Button } from '../../../shared/ui'
import { stepWarnings } from '../../../lib/outreach/merge'

export interface StepDraft {
  key: number
  dayOffset: string
  subject: string
  body: string
  sameThread: boolean
}

export type StepField = 'subject' | 'body'
type FieldEl = HTMLInputElement | HTMLTextAreaElement

interface Props {
  step: StepDraft
  index: number
  count: number
  selected: boolean
  readOnly: boolean
  phrases: readonly string[]
  customKeys: readonly string[]
  error?: string | undefined
  onChange: (patch: Partial<StepDraft>) => void
  onSelect: () => void
  onTrack: (field: StepField, el: FieldEl) => void
  register: (field: StepField, el: FieldEl | null) => void
  onMove: (dir: -1 | 1) => void
  onRemove: () => void
}

export function StepCard({
  step,
  index,
  count,
  selected,
  readOnly,
  phrases,
  customKeys,
  error,
  onChange,
  onSelect,
  onTrack,
  register,
  onMove,
  onRemove,
}: Props) {
  const n = index + 1
  const id = `step-${step.key}`
  const warn = stepWarnings(step, phrases, customKeys)

  const track = (field: StepField) => (e: SyntheticEvent<FieldEl>) =>
    onTrack(field, e.currentTarget)
  const edit = (field: StepField) => (e: ChangeEvent<FieldEl>) => {
    onChange(field === 'subject' ? { subject: e.target.value } : { body: e.target.value })
    onTrack(field, e.target)
  }

  return (
    <section
      className={`os-step${selected ? ' os-step-selected' : ''}`}
      aria-label={`Step ${n}`}
      onFocusCapture={onSelect}
      onClick={onSelect}
    >
      <div className="os-bar">
        <strong className="os-name">Step {n}</strong>
        {!readOnly && (
          <span className="os-actions os-bar-end">
            <Button
              variant="secondary"
              disabled={index === 0}
              aria-label={`Move step ${n} up`}
              onClick={() => onMove(-1)}
            >
              Up
            </Button>
            <Button
              variant="secondary"
              disabled={index === count - 1}
              aria-label={`Move step ${n} down`}
              onClick={() => onMove(1)}
            >
              Down
            </Button>
            <Button variant="danger" aria-label={`Remove step ${n}`} onClick={onRemove}>
              Remove
            </Button>
          </span>
        )}
      </div>

      <div className="settings-row os-field">
        <label className="settings-label" htmlFor={`${id}-offset`}>
          Days after the previous step
        </label>
        <input
          id={`${id}-offset`}
          className="settings-input"
          type="number"
          min={0}
          disabled={readOnly}
          value={step.dayOffset}
          onChange={(e) => onChange({ dayOffset: e.target.value })}
        />
        {index === 0 && (
          <div className="settings-hint">For step 1 this is days after enrolment.</div>
        )}
      </div>

      <div className="settings-row os-field">
        <label className="settings-label" htmlFor={`${id}-subject`}>
          Subject
        </label>
        <input
          id={`${id}-subject`}
          className="settings-input"
          disabled={readOnly}
          ref={(el) => register('subject', el)}
          value={step.subject}
          onChange={edit('subject')}
          onFocus={track('subject')}
          onSelect={track('subject')}
          onKeyUp={track('subject')}
          onClick={track('subject')}
        />
      </div>

      <div className="settings-row os-field">
        <label className="settings-label" htmlFor={`${id}-body`}>
          Body
        </label>
        <textarea
          id={`${id}-body`}
          className="settings-input"
          rows={6}
          disabled={readOnly}
          ref={(el) => register('body', el)}
          value={step.body}
          onChange={edit('body')}
          onFocus={track('body')}
          onSelect={track('body')}
          onKeyUp={track('body')}
          onClick={track('body')}
        />
      </div>

      <label className="os-check" htmlFor={`${id}-thread`}>
        <input
          id={`${id}-thread`}
          type="checkbox"
          disabled={readOnly || index === 0}
          checked={index === 0 ? false : step.sameThread}
          onChange={(e) => onChange({ sameThread: e.target.checked })}
        />
        Reply in the same thread
      </label>

      {warn.blocked.length > 0 && (
        <div className="os-warn">Blocked phrases found: {warn.blocked.join(', ')}</div>
      )}
      {warn.unknownFields.length > 0 && (
        <div className="os-warn">
          Unknown merge fields: {warn.unknownFields.map((f) => `{{${f}}}`).join(', ')}
        </div>
      )}
      {error && (
        <div className="os-error" role="alert">
          {error}
        </div>
      )}
    </section>
  )
}
